import React, { useState, useEffect, useRef } from 'react';

/**
 * AssignPills — inline assignment for a pipeline card.
 *
 * Replaces the old modal + "Reassign" button. Assigned users show as their
 * profile avatars; hovering shows the name. Clicking an avatar extends it into
 * a pill with an X so a single click removes that one person — no modal, no
 * reassign round-trip. A dashed "+" opens an inline searchable picker.
 *
 * Props:
 *   cardId
 *   assignedUsers   [{ id, name, avatar_url }]
 *   onChanged()     called after any assign/unassign so the parent can reload
 */
const AssignPills = ({ cardId, assignedUsers = [], onChanged }) => {
  const [picking, setPicking] = useState(false);
  const [team, setTeam] = useState([]);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef(null);

  const headers = () => ({
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json',
  });

  // Load the assignable team only when the picker first opens.
  useEffect(() => {
    if (!picking || team.length) return;
    (async () => {
      try {
        const res = await fetch('/api/user-management/team', { headers: headers() });
        if (!res.ok) return;
        const ct = res.headers.get('content-type') || '';
        if (!ct.includes('application/json')) return;
        const data = await res.json();
        setTeam(Array.isArray(data) ? data : (data.users || data.team || []));
      } catch { /* leave empty */ }
    })();
  }, [picking, team.length]);

  // Close picker / collapse confirm on outside click.
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setPicking(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const assign = async (userId) => {
    setBusy(true);
    try {
      const res = await fetch(`/api/cards/${cardId}/assign`, {
        method: 'POST', headers: headers(), body: JSON.stringify({ user_id: userId }),
      });
      if (res.ok) { setQuery(''); setPicking(false); onChanged?.(); }
    } catch { /* noop */ } finally { setBusy(false); }
  };

  const unassign = async (userId) => {
    setBusy(true);
    try {
      const res = await fetch(`/api/cards/${cardId}/assign/${userId}`, {
        method: 'DELETE', headers: headers(),
      });
      if (res.ok) { onChanged?.(); }
    } catch { /* noop */ } finally { setBusy(false); }
  };

  const assignedIds = new Set(assignedUsers.map(u => u.id));
  const matches = team
    .filter(u => !assignedIds.has(u.id))
    .filter(u => {
      const q = query.trim().toLowerCase();
      return !q || (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
    })
    .slice(0, 30);

  const initials = (name) => (name || '?').trim().charAt(0).toUpperCase();

  return (
    <div ref={wrapRef} className="relative">
      <div className="flex items-center gap-1.5 flex-wrap">
        {/* Each assignment is always a pill: avatar + an always-visible X.
            No click-to-expose — removal is one click, no intermediate step. */}
        {assignedUsers.map(u => (
          <span key={u.id} className="inline-flex items-center bg-blue-50 rounded-full pr-1" title={u.name}>
            <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-[9px] font-bold flex items-center justify-center overflow-hidden">
              {u.avatar_url ? <img src={u.avatar_url} alt="" className="w-6 h-6 rounded-full object-cover" /> : initials(u.name)}
            </span>
            <button onClick={() => unassign(u.id)} disabled={busy}
              className="ml-0.5 text-blue-400 hover:text-blue-700 disabled:opacity-50" title={`Remove ${u.name}`}>
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </span>
        ))}

        {/* Add */}
        <button onClick={() => setPicking(p => !p)}
          className="w-6 h-6 rounded-full border border-dashed border-gray-300 text-gray-400 hover:border-blue-300 hover:text-blue-500 flex items-center justify-center"
          title="Assign someone">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </button>
      </div>

      {/* Inline picker */}
      {picking && (
        <div className="absolute z-20 mt-1 left-0 w-56 bg-white border border-gray-200 rounded-lg shadow-lg p-2">
          <input
            type="text" autoFocus value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search team…"
            className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <div className="mt-1 max-h-44 overflow-y-auto">
            {matches.length === 0 ? (
              <p className="text-[11px] text-gray-400 text-center py-3">
                {team.length === 0 ? 'Loading…' : 'No one to add'}
              </p>
            ) : matches.map(u => (
              <button key={u.id} onClick={() => assign(u.id)} disabled={busy}
                className="w-full flex items-center gap-2 px-2 py-1.5 text-left rounded hover:bg-blue-50 disabled:opacity-50">
                <span className="w-6 h-6 rounded-full bg-gray-100 text-gray-600 text-[9px] font-bold flex items-center justify-center overflow-hidden flex-shrink-0">
                  {u.avatar_url ? <img src={u.avatar_url} alt="" className="w-6 h-6 rounded-full object-cover" /> : initials(u.name)}
                </span>
                <span className="min-w-0">
                  <span className="block text-xs text-gray-800 truncate">{u.name}</span>
                  {u.email && <span className="block text-[10px] text-gray-400 truncate">{u.email}</span>}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignPills;
